import Config from './Config'

// Sanitized breadcrumb logger for the payment flow (approval.md locks 12–14).
// Recursive allowlist: only SAFE_KEYS survive, at any depth — token/signature/PAN/PII
// can never reach a console. Prod logs event names only; other envs log event + data.

export type PaymentEvent =
  | 'apple-pay.validation-requested'
  | 'apple-pay.validated'
  | 'apple-pay.authorized'
  | 'apple-pay.completed'
  | 'apple-pay.cancelled'
  | 'apple-pay.timed-out'
  | 'apple-pay.failed'

// Never add a token, signature, nonce, PAN, or PII field name here.
const SAFE_KEYS: ReadonlySet<string> = new Set([
  'countryCode', 'currencyCode', 'label', 'amount', 'total',
  'merchantSessionIdentifier', 'merchantIdentifier', 'domainName',
  'displayName', 'epochTimestamp', 'expiresAt', 'code',
])

function sanitize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitize)
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (SAFE_KEYS.has(key)) out[key] = sanitize(v)
    }
    return out
  }
  return value
}

function isVerbose(): boolean {
  return Config.ENV !== 'prod'
}

function errorCode(err: unknown): string | undefined {
  if (err !== null && typeof err === 'object') {
    const code = (err as { code?: unknown }).code
    if (typeof code === 'string') return code
  }
  return undefined
}

export const logger = {
  breadcrumb(event: PaymentEvent, data?: unknown): void {
    if (!isVerbose() || data === undefined) {
      console.log(event)
      return
    }
    console.log(event, sanitize(data))
  },

  error(event: PaymentEvent, err: unknown): void {
    const code = isVerbose() ? errorCode(err) : undefined
    if (code === undefined) {
      console.error(event)
      return
    }
    console.error(event, { code })
  },
}
